/** Cross-cutting domain constants shared by api + web. */

export const LOCALES = ['he', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'he';

/** All times stored UTC; rendered in this zone. */
export const TIMEZONE = 'Asia/Jerusalem';

/**
 * Marketplace/service categories.
 *
 * `barber` was merged into `hair` in 2026-07 and un-merged in 2026-07-30: a
 * barbershop is a different destination from a salon, not just a gender tag on
 * one. The old merge rewrote every `barber` row to `hair` and that is not
 * reversible, so the re-added key starts empty rather than restored.
 *
 * **Append only.** Array position is the display order at every call site, and
 * `VERTICALS[0]` is the default vertical for a new service - prepending would
 * silently change it.
 */
export const BUSINESS_CATEGORIES = [
  'hair',
  'nails',
  'lashes_brows',
  'makeup',
  'facial',
  'massage',
  'spa',
  'barber',
  'hair_removal',
  'holistic',
  'dental',
  'pet_grooming',
] as const;
export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number];

/**
 * Per-service category (`Service.vertical`). Same value set as
 * BUSINESS_CATEGORIES — historically a narrower 4-value list, widened
 * 2026-07 to close the gap where a massage/spa/makeup business had no
 * correct vertical to pick for its own services, and again 2026-07-30.
 *
 * `service.vertical` carries a SQL CHECK constraint listing these values, so
 * adding one here needs a hand-authored migration to widen it.
 */
export const VERTICALS = BUSINESS_CATEGORIES;
export type Vertical = (typeof VERTICALS)[number];

/** Per-service client-gender tag — search filter only, hair/nails in the UI. */
export const SERVICE_GENDERS = ['men', 'women', 'unisex'] as const;
export type ServiceGender = (typeof SERVICE_GENDERS)[number];

/**
 * Marketplace price filter: a single upper bound on a business's STARTING
 * price (its cheapest active priced service), in ILS.
 *
 * Replaced a three-band ₪/₪₪/₪₪₪ enum on 2026-08-15. Two things were wrong
 * with the bands and only one of them was the label: a band matched if ANY
 * one service fell inside it, so a salon with a single ₪25 add-on answered
 * "cheap", and the result card showed no price at all, so nothing on screen
 * could contradict it. A cap on the starting price is one number, the same
 * number the card prints.
 *
 * MIN/MAX/STEP live here because the slider (mobile) and the input validation
 * (api) have to agree on the scale - a client that can pick ₪20 against a
 * server that rejects it is a silently dead filter. MAX is "no limit" on the
 * client: at the top of the track it sends no filter at all rather than a
 * cap, so a business priced above it is still reachable.
 */
export const PRICE_FILTER = { MIN: 50, MAX: 500, STEP: 10 } as const;

/**
 * Marketplace search-filter minimum-rating thresholds — a business's
 * LATERAL-joined `rating_avg` must be >= the chosen tier. Unrated businesses
 * (`rating_avg IS NULL`) never match any tier.
 */
export const RATING_TIERS = [4, 4.5] as const;
export type RatingTier = (typeof RATING_TIERS)[number];

/** Booking attribution source — NOT NULL on every booking (load-bearing for P1 commission). */
export const BOOKING_SOURCES = [
  'direct_link',
  'widget',
  'marketplace_search',
  'marketplace_map',
  'staff_manual',
  'ai_whatsapp',
  'ai_chat',
  'walk_in',
] as const;
export type BookingSource = (typeof BOOKING_SOURCES)[number];

/**
 * Every role name the platform uses, across two different axes — this is a
 * vocabulary, not one enum. `client` and `platform_admin` are properties of an
 * account; the other four are a MEMBERSHIP of one business, and are the only
 * ones `membership.role` may hold.
 *
 * `secretary` was missing here from 2026-08-13 until 2026-08-22 (#69). It had
 * been added to the DB CHECK and to all four `Role` unions in the apps, but not
 * to this list, whose comment claimed it was the enforced set. Nothing in
 * `apps/` imports it, so the mistake stayed invisible — which is exactly the
 * danger: the next caller to reach for it would have got a vocabulary that
 * silently excluded every secretary.
 *
 * The membership subset is now held to the live `membership_role_check`
 * constraint by `apps/api/test/role-vocabulary.test.ts`, along with the four
 * duplicated unions. Those duplicates are deliberate — `trpc/context.ts` cannot
 * import from the Nest side (web-clean), and mobile cannot import from the api
 * at all — so the guard exists instead of a shared import.
 */
export const ROLES = [
  'client',
  'staff',
  'secretary',
  'manager',
  'business_owner',
  'platform_admin',
] as const;
export type Role = (typeof ROLES)[number];

/**
 * Cancellation & reschedule window options (minutes before the appointment
 * below which the policy applies); 0 = "flexible, no cutoff". MANDATORY:
 * business.updateProfile rejects isPublished:true while a business's
 * cancellationWindowMinutes is null. Display-only for v1 — not enforced in
 * cancel/reschedule logic.
 */
export const CANCELLATION_WINDOW_OPTIONS_MIN = [0, 60, 120, 360, 720, 1440, 2880, 4320] as const;
export type CancellationWindowMinutes = (typeof CANCELLATION_WINDOW_OPTIONS_MIN)[number];

/**
 * Late-arrival grace period options (minutes); 0 = no grace period. Optional
 * — a business with this unset simply omits the row on customer-facing
 * displays. Display-only for v1.
 */
export const LATE_ARRIVAL_GRACE_OPTIONS_MIN = [0, 5, 10, 15, 20, 30] as const;
export type LateArrivalGraceMinutes = (typeof LATE_ARRIVAL_GRACE_OPTIONS_MIN)[number];

/**
 * Informational no-show consequence shown to the customer. This stack has no
 * payment provider anywhere, so 'fee_may_apply' is disclosure text only —
 * never an enforced charge. Optional; backed by a DB CHECK constraint.
 */
export const NO_SHOW_POLICY_VALUES = ['no_penalty', 'may_restrict', 'fee_may_apply'] as const;
export type NoShowPolicy = (typeof NO_SHOW_POLICY_VALUES)[number];

/**
 * Minimum booking notice options (minutes before a slot a client may book at
 * all); 0 = no minimum. Display-only for v1 — nothing currently blocks a
 * booking inside this window; see the design spec's known-limitation note.
 * Optional.
 */
export const MIN_BOOKING_NOTICE_OPTIONS_MIN = [0, 60, 120, 360, 1440] as const;
export type MinBookingNoticeMinutes = (typeof MIN_BOOKING_NOTICE_OPTIONS_MIN)[number];

/**
 * How far ahead the booking wizard searches for availability, in days —
 * also the horizon a waitlist entry's `expiresAt` is set to (join time +
 * this many days).
 *
 * One number, two jobs, so they cannot be allowed to differ: a wizard
 * searching a different window than the waitlist expires on would offer a
 * waitlist for days it never looked at. The mobile wizard
 * (apps/mobile/src/business/wizard/CalendarTimeStep.tsx) used to hardcode 31
 * separately and this note asked a reader to keep the two in sync by hand; it
 * imports this now, so there is nothing left to keep in sync.
 */
export const WAITLIST_WINDOW_DAYS = 31;

/**
 * Hard byte cap on a business photo/logo/staff-photo upload
 * (business.createUploadUrl / admin.createUploadUrl). The client
 * (business-image-upload.ts) already downscales+JPEG-compresses every image
 * before upload — this is a defense-in-depth ceiling against a malicious or
 * buggy client PUTing something huge straight to the signed URL, not a
 * normal-path limit. Enforced via GCS's `x-goog-content-length-range`
 * extension header: the value baked into the SIGNED URL
 * (gcs-storage.service.ts) and the value the client sends as an actual
 * request header on the PUT (uploadToSignedUrl) must be byte-identical, or
 * V4 signature verification rejects the request — that's why this lives in
 * the framework-free shared package instead of api-only or mobile-only.
 */
export const MAX_UPLOAD_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * The one pipeline vocabulary shared by a `lead` (a prospect that is not on the
 * platform yet) and a `business.onboarding_stage`. Both appear in the same
 * columns on the admin console's pipeline screen, so they must speak the same
 * words — a lead that converts keeps its place in the funnel instead of
 * restarting it.
 *
 * `prospect` → `contacted` describe someone who has no business row at all;
 * `created` onward describe one that does. NULL on a business means "infer from
 * the activation ladder" (has services / has staff / has hours / has a booking),
 * which is what the console does when nobody has set a stage by hand.
 *
 * Backed by a DB CHECK on `business.onboarding_stage` and on `lead.stage`.
 */
export const ONBOARDING_STAGES = [
  'prospect',
  'contacted',
  'created',
  'setting_up',
  'live',
  'active',
  'dormant',
] as const;
export type OnboardingStage = (typeof ONBOARDING_STAGES)[number];

/** How a lead reached us. `landing` is the public form; the rest are hand-entered. */
export const LEAD_SOURCES = [
  'landing',
  'referral',
  'instagram',
  'facebook',
  'whatsapp',
  'phone',
  'walk_in',
  'other',
] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

/** Timeline entry kinds on `activity`. `system` rows are written by the server, never typed. */
export const ACTIVITY_KINDS = ['note', 'call', 'whatsapp', 'meeting', 'email', 'system'] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

/** Task lifecycle. Lists only — there is deliberately no board. */
export const TASK_STATUSES = ['todo', 'doing', 'blocked', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Task priority, low to high. */
export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
